/**
 * Table sync / merge guards — Ticket #26425 (Zest Bar merge wipe).
 *
 * docs/design-docs/2026-08-01-ticket-26425-client-merge-investigation.md
 * docs/design-docs/implementation-checklist-26425.md
 * Companion design:
 *   Backend/Online Sales Server Api/docs/design-docs/2026-08-01-ticket-26425-fix-gap-analysis.md
 *   (Marcus Chen — command-aware 409, merge response shape, hydration)
 *
 * Pure helpers so unit specs can run with `node` (no Vue/webpack).
 * NEVER POST items:[] for an ordinary save of an existing SaleNum —
 * server delete-all-then-insert wipes merged lines.
 * NEVER synthesize merge table names — operator must confirm/edit the name.
 * ALWAYS send command on save payloads (default "save").
 */

/** Ordinary full-replacement save (empty-items guard applies). */
export const TABLE_COMMAND_SAVE = 'save';
/** Split result may intentionally be empty — do not block. */
export const TABLE_COMMAND_SPLIT = 'split';
/** Explicit void path. */
export const TABLE_COMMAND_VOID = 'void';
/** Explicit clear path (audited; not ordinary save). */
export const TABLE_COMMAND_CLEAR = 'clear';

/**
 * Operator-facing message when empty replace is blocked after re-fetch
 * on a sale that already exists (wipe guard — ticket #26425).
 * Keep plain language for waiters (portfolio USER-FACING-ERRORS).
 * NEVER surface raw BE text such as "Table Does not Exist".
 */
export const EMPTY_TABLE_SYNC_BLOCKED_MSG =
    'This table still has items. Refresh to load them.';

/**
 * When GET/POST savetable fails because the table row is gone or never existed
 * for this sale number. Operator-safe — never surface "Table Does not Exist".
 * Founder + Victoria brief 2026-08-09 (Joseph WaiterStation).
 */
export const TABLE_NOT_READY_MSG =
    'This table is no longer available. Refresh and try again.';

/**
 * 409 EMPTY_SAVE_REJECTED — server still has lines; FE tried empty replace.
 * Auto-recovery path re-fetches; this is the toast while that runs.
 */
export const EMPTY_SAVE_REJECTED_MSG =
    'This table still has items. Refresh to load them.';

/** Operator message when 409 conflict forces a refresh (Victoria brief + Marcus §6.2). */
export const TABLE_STATE_CONFLICT_MSG =
    'Table was modified by another user. Re-fetching...';

/** Operator-facing message when merge name is blank. */
export const MERGE_TABLE_NAME_REQUIRED_MSG =
    'Enter a name for the merged table before continuing.';

/** Server conflict codes accepted for empty/stale save (Victoria + Marcus). */
export const EMPTY_SAVE_CONFLICT_CODES = [
    'EMPTY_SAVE_REJECTED',
    'TABLE_STATE_CONFLICT',
];

/**
 * True when this payload would empty-replace an existing open table.
 * Ticket #26425 — SaleNum > 0 with zero items is the wipe vector.
 *
 * @param {number|string|null|undefined} saleNum
 * @param {Array|null|undefined} items
 * @returns {boolean}
 */
export function isDangerousEmptyItemsSync(saleNum, items) {
    const n = Number(saleNum || 0);
    if (!Number.isFinite(n) || n <= 0) return false;
    return !Array.isArray(items) || items.length === 0;
}

/**
 * Normalize table command for the server contract.
 * Defaults to "save" for older callers that omit command (backwards-compat).
 * Ticket #26425 Fix A — Marcus gap-analysis §4 / Victoria brief.
 *
 * @param {unknown} command
 * @returns {'save'|'split'|'void'|'clear'|string}
 */
export function resolveTableCommand(command) {
    if (command == null || command === '') return TABLE_COMMAND_SAVE;
    const c = String(command).trim().toLowerCase();
    if (!c) return TABLE_COMMAND_SAVE;
    return c;
}

/**
 * Whether ordinary-save empty-items guard should block this payload.
 * Split/void/clear may legitimately carry empty items; ordinary save of an
 * *existing* SaleNum with items=[] must not (wipe vector — ticket #26425).
 *
 * Founder 2026-08-09 (Joseph WaiterStation): when saleNum was JUST allocated
 * in this call (create path), do NOT block — BE only reserves the number;
 * the empty POST is what creates the table row. Blocking create caused
 * "Table Does not Exist" on later paths that assumed the header existed.
 *
 * @param {unknown} command
 * @param {number|string|null|undefined} saleNum
 * @param {Array|null|undefined} items
 * @param {{ saleNumJustAllocated?: boolean }} [opts]
 * @returns {boolean}
 */
export function shouldBlockEmptySave(command, saleNum, items, opts = {}) {
    // Create path: allow empty POST so BE can create the table header.
    if (opts && opts.saleNumJustAllocated === true) return false;
    const cmd = resolveTableCommand(command);
    if (cmd !== TABLE_COMMAND_SAVE) return false;
    return isDangerousEmptyItemsSync(saleNum, items);
}

/**
 * Detect BE/local "table missing" failures from hydrate / savetable GET.
 * Used so empty-save hydrate never surfaces raw "Table Does not Exist".
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTableMissingError(err) {
    if (!err) return false;
    if (err.code === 'TABLE_MISSING' || err.code === 'EMPTY_SAVE_BLOCKED') return true;
    const status = Number(err?.response?.status || err?.status || 0);
    // Portfolio rule: business missing-record is often 406; some paths still 404.
    if (status === 404 || status === 406 || status === 409) {
        const body = err?.response?.data;
        const fromBody = typeof body === 'string'
            ? body
            : (body && typeof body === 'object'
                ? (body.error || body.Error || body.message || body.Message || '')
                : '');
        if (/table\s+does\s+not\s+exist|table\s+not\s+found|no\s+matching|empty_save/i.test(String(fromBody))) {
            return true;
        }
        // 404/406 on savetable hydrate without body still means missing table shell
        if (status === 404 || status === 406) return true;
    }
    const msg = String(err?.message || err || '');
    return /table\s+does\s+not\s+exist|table\s+not\s+found|table\s+not\s+available/i.test(msg);
}

/**
 * Normalize a table name for compare / validation.
 * @param {unknown} name
 * @returns {string}
 */
export function normalizeTableName(name) {
    if (name == null) return '';
    return String(name).trim();
}

/**
 * Require a non-blank merge table name (Founder: NO auto-rename / no "merged X").
 * Ticket #26425 Fix B.
 *
 * @param {unknown} tableName
 * @returns {string} trimmed name
 * @throws {Error} with code MERGE_TABLE_NAME_REQUIRED when blank
 */
export function requireMergeTableName(tableName) {
    const name = normalizeTableName(tableName);
    if (!name) {
        const err = new Error(MERGE_TABLE_NAME_REQUIRED_MSG);
        err.code = 'MERGE_TABLE_NAME_REQUIRED';
        throw err;
    }
    return name;
}

/**
 * Decide whether `Name` should be included on a table save payload.
 * Ticket #26425 Fix C — omit Name when unchanged so the server does not rewrite it.
 *
 * @param {unknown} proposedName - name about to be sent
 * @param {unknown} previousName - known server/local name before this edit
 * @param {{ forceRename?: boolean }} [opts] - when true, always include Name (explicit rename session)
 * @returns {{ includeName: boolean, name: string|null, rename: boolean }}
 */
export function resolveNameForSyncPayload(proposedName, previousName, opts = {}) {
    const proposed = normalizeTableName(proposedName);
    const previous = normalizeTableName(previousName);
    const forceRename = opts && opts.forceRename === true;

    if (!proposed) {
        // No name to send — leave server name alone (do not synthesize "Table #n").
        return { includeName: false, name: null, rename: false };
    }
    if (!forceRename && previous && proposed.toLowerCase() === previous.toLowerCase()) {
        return { includeName: false, name: previous, rename: false };
    }
    // Explicit rename (or first known name / forceRename session marker).
    return { includeName: true, name: proposed, rename: true };
}

/**
 * Extract items array from a getTableData / savetable response shape.
 * @param {object|null|undefined} serverTable
 * @returns {Array}
 */
export function extractServerTableItems(serverTable) {
    if (!serverTable || typeof serverTable !== 'object') return [];
    if (Array.isArray(serverTable.items)) return serverTable.items;
    if (Array.isArray(serverTable.Items)) return serverTable.Items;
    if (Array.isArray(serverTable.saleLines)) return serverTable.saleLines;
    return [];
}

/**
 * Resolve server-confirmed name from a save/merge response.
 * Server is authoritative for the persisted name (Fix C).
 *
 * @param {object|null|undefined} response
 * @param {unknown} fallbackLocalName
 * @returns {string|null} name to apply locally, or null if no change signal
 */
export function resolveConfirmedNameFromResponse(response, fallbackLocalName) {
    if (!response || typeof response !== 'object') return null;
    const fromServer = normalizeTableName(
        response.Name ?? response.name ?? response.tableName ?? null
    );
    if (!fromServer) return null;
    const local = normalizeTableName(fallbackLocalName);
    if (local && fromServer.toLowerCase() === local.toLowerCase()) {
        return local;
    }
    return fromServer;
}

/**
 * Find a table in a list by exact name (case-insensitive), preferring richest items.
 * Used after merge when backend returns only "Done" (no saleNum yet).
 * Ticket #26425 Fix D + Marcus §5 response compatibility fallback.
 *
 * @param {Array} tables
 * @param {string} tableName
 * @returns {object|null}
 */
export function findTableByName(tables, tableName) {
    const want = normalizeTableName(tableName).toLowerCase();
    if (!want || !Array.isArray(tables)) return null;
    const matches = tables.filter((t) => {
        const n = normalizeTableName(t?.Name ?? t?.name ?? t?.tableName).toLowerCase();
        return n === want;
    });
    if (!matches.length) return null;
    matches.sort((a, b) => {
        const ai = extractServerTableItems(a).length;
        const bi = extractServerTableItems(b).length;
        return bi - ai;
    });
    return matches[0];
}

/**
 * Sale number from a table row / getTableData payload.
 * @param {object|null|undefined} table
 * @returns {number}
 */
export function extractSaleNum(table) {
    if (!table || typeof table !== 'object') return 0;
    const n = Number(table.SaleNum ?? table.saleNumber ?? table.saleNum ?? 0);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * Parse merge HTTP body — supports legacy string "Done" and new object shape
 * `{ saleNum, name, revision, itemCount, status }`.
 * Marcus gap-analysis §5 / §6.1 — response-shape compatibility.
 *
 * @param {unknown} data - axios response.data
 * @returns {{
 *   legacyString: boolean,
 *   saleNum: number,
 *   name: string|null,
 *   revision: number|null,
 *   itemCount: number|null,
 *   status: string|null,
 *   raw: unknown
 * }}
 */
export function parseMergeResponse(data) {
    if (typeof data === 'string') {
        return {
            legacyString: true,
            saleNum: 0,
            name: null,
            revision: null,
            itemCount: null,
            status: data,
            raw: data,
        };
    }
    if (data && typeof data === 'object') {
        const saleNum = extractSaleNum(data);
        return {
            legacyString: false,
            saleNum,
            name: normalizeTableName(data.name ?? data.Name ?? data.tableName) || null,
            revision:
                data.revision != null && Number.isFinite(Number(data.revision))
                    ? Number(data.revision)
                    : null,
            itemCount:
                data.itemCount != null && Number.isFinite(Number(data.itemCount))
                    ? Number(data.itemCount)
                    : null,
            status: data.status != null ? String(data.status) : data.message != null ? String(data.message) : null,
            raw: data,
        };
    }
    return {
        legacyString: false,
        saleNum: 0,
        name: null,
        revision: null,
        itemCount: null,
        status: null,
        raw: data,
    };
}

/**
 * Detect HTTP 409 empty/stale table save conflict from an axios-style error.
 * Codes: EMPTY_SAVE_REJECTED (Victoria brief) | TABLE_STATE_CONFLICT (Marcus §6.2).
 *
 * @param {object|null|undefined} err
 * @returns {boolean}
 */
export function isEmptySaveConflictError(err) {
    const status = Number(err?.response?.status || err?.status || 0);
    if (status !== 409) return false;
    const body = err?.response?.data ?? err?.data ?? {};
    const code = String(body?.code ?? body?.errorCode ?? body?.error ?? '').trim();
    if (EMPTY_SAVE_CONFLICT_CODES.includes(code)) return true;
    // Some gateways only put the code in the message string.
    const msg = String(body?.message ?? err?.message ?? '');
    return EMPTY_SAVE_CONFLICT_CODES.some((c) => msg.includes(c));
}

/**
 * Extract conflict body metadata for logging / UI (saleNum, revision, serverItemCount).
 * @param {object|null|undefined} err
 * @returns {{ saleNum: number, revision: number|null, serverItemCount: number|null, message: string }}
 */
export function extractConflictMeta(err) {
    const body = err?.response?.data ?? err?.data ?? {};
    return {
        saleNum: extractSaleNum(body),
        revision:
            body?.revision != null && Number.isFinite(Number(body.revision))
                ? Number(body.revision)
                : null,
        serverItemCount:
            body?.serverItemCount != null && Number.isFinite(Number(body.serverItemCount))
                ? Number(body.serverItemCount)
                : null,
        message: String(body?.message || TABLE_STATE_CONFLICT_MSG),
    };
}

/**
 * Apply name field rules onto a mutable save payload object.
 * Mutates `payload` in place: may delete Name/name, set rename marker.
 * Ticket #26425 Fix C.
 *
 * @param {object} payload
 * @param {unknown} previousName
 * @param {{ forceRename?: boolean }} [opts]
 * @returns {object} the same payload
 */
export function applyNameToSyncPayload(payload, previousName, opts = {}) {
    if (!payload || typeof payload !== 'object') return payload;
    const proposed = payload.Name ?? payload.name ?? null;
    const resolved = resolveNameForSyncPayload(proposed, previousName, opts);
    if (!resolved.includeName) {
        delete payload.Name;
        delete payload.name;
        delete payload.rename;
    } else {
        payload.Name = resolved.name;
        payload.name = resolved.name;
        if (resolved.rename) {
            payload.rename = true;
        }
    }
    return payload;
}

/**
 * Restore Name onto a local/offline write payload after Fix C omitted it.
 * Cloud omit-when-unchanged is correct for POST; local SQLite full-replaces
 * table_data, so a nameless payload wiped the display name on second save
 * (offline create → open → add item → save).
 *
 * @param {object} payload - mutable local write body
 * @param {unknown} fallbackName - previousName / openTableName / prior payload Name
 * @returns {object} same payload
 */
export function preserveNameOnLocalTableWrite(payload, fallbackName) {
    if (!payload || typeof payload !== 'object') return payload;
    const current = normalizeTableName(payload.Name ?? payload.name);
    if (current) return payload;
    const keep = normalizeTableName(fallbackName);
    if (!keep) return payload;
    payload.Name = keep;
    payload.name = keep;
    return payload;
}

/**
 * Merge pending (not-yet-cloud) local open tables into the API list for UI.
 * Online Main used API-only display, so offline-created checks vanished until
 * outbox drained — operator could not find the table after reconnect.
 *
 * @param {Array} apiTables - cloud open tables
 * @param {Array} localTables - offline_tables rows (parsed)
 * @returns {Array} union, API rows win on same SaleNum
 */
export function mergePendingLocalTablesIntoApiList(apiTables, localTables) {
    const api = Array.isArray(apiTables) ? apiTables : [];
    const local = Array.isArray(localTables) ? localTables : [];
    const bySale = new Map();
    for (const t of api) {
        const sn = String(t?.saleNumber ?? t?.SaleNum ?? '').trim();
        if (!sn) continue;
        bySale.set(sn, t);
    }
    for (const t of local) {
        const sn = String(t?.saleNumber ?? t?.SaleNum ?? '').trim();
        if (!sn) continue;
        if (bySale.has(sn)) continue;
        const synced = t?.synced === true || t?.synced === 'true';
        if (synced) continue;
        bySale.set(sn, t);
    }
    return Array.from(bySale.values());
}

/**
 * Ensure every save payload carries `command` (default save) and optional revision.
 * Ticket #26425 Fix A — server contract.
 *
 * @param {object} payload
 * @param {{ command?: string, expectedRevision?: number|null }} [opts]
 * @returns {object}
 */
export function applyCommandToSyncPayload(payload, opts = {}) {
    if (!payload || typeof payload !== 'object') return payload;
    const command = resolveTableCommand(
        opts.command != null ? opts.command : payload.command
    );
    payload.command = command;
    const rev =
        opts.expectedRevision != null
            ? opts.expectedRevision
            : payload.expectedRevision != null
              ? payload.expectedRevision
              : payload.revision != null
                ? payload.revision
                : null;
    if (rev != null && Number.isFinite(Number(rev))) {
        payload.expectedRevision = Number(rev);
    }
    return payload;
}

/**
 * True when Save/Pay must be blocked because the open sale has no local items.
 * Ticket #26425 Fix D — block Save/Pay on SaleNum > 0 with empty items.
 *
 * @param {number|string|null|undefined} saleNum
 * @param {Array|null|undefined} items
 * @returns {boolean}
 */
export function shouldBlockSavePayEmptyExisting(saleNum, items) {
    return isDangerousEmptyItemsSync(saleNum, items);
}
